import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import { useViewAs } from '../contexts/ViewAsContext';

export const OPEN_TICKET_STATUSES = ['open', 'in_progress', 'waiting_on_customer'] as const;
export const CLOSED_TICKET_STATUSES = ['resolved', 'closed'] as const;

type TicketStatus = 'open' | 'in_progress' | 'waiting_on_customer' | 'resolved' | 'closed';

export interface TicketRecord {
  id: string;
  ticket_number: string;
  customer_id: string;
  location_id: string | null;
  deployment_id: string | null;
  asset_id: string | null;
  title: string;
  description: string | null;
  status: string | null;
  priority: string | null;
  source: string | null;
  sla_deadline: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  location: { id: string; name: string } | null;
  deployment: { id: string; solution_type: string } | null;
  customer: { id: string; name: string } | null;
}

interface TicketFilters {
  status?: TicketStatus | readonly TicketStatus[];
}

/**
 * Allowlist: no SELECT *, no join to staff. assigned_to_user_id (internal
 * staff identity) is deliberately omitted; conversations live in the
 * support system.
 */
export const TICKET_SELECT = `
  id, ticket_number, customer_id, location_id, deployment_id, asset_id,
  title, description, status, priority, source, sla_deadline,
  resolution_notes, resolved_at, created_at, updated_at,
  location:locations(id, name),
  deployment:deployments(id, solution_type),
  customer:customers(id, name)
`;

export function isOpenStatus(status: string | null | undefined): boolean {
  return (OPEN_TICKET_STATUSES as readonly string[]).includes(status ?? '');
}

/** Tickets for the current account scope (portfolio → every linked account). */
export function useTickets(filters?: TicketFilters) {
  const scope = useCustomerScope();
  const statusKey = Array.isArray(filters?.status)
    ? filters.status.join(',')
    : filters?.status ?? '';

  return useQuery({
    queryKey: ['tickets', scope.key, statusKey],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<TicketRecord[]> => {
      let query = supabase
        .from('tickets_customer_view')
        .select(TICKET_SELECT)
        .in('customer_id', scope.ids)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        if (typeof filters.status === 'string') {
          query = query.eq('status', filters.status);
        } else {
          query = query.in('status', [...filters.status]);
        }
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as TicketRecord[];
    },
    staleTime: 30 * 1000,
  });
}

export interface CreateTicketInput {
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  customer_id: string;
  location_id?: string;
  deployment_id?: string;
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const { isStaff, viewAsUserId } = useViewAs();
  const readOnly = isStaff && !!viewAsUserId;

  return useMutation({
    mutationFn: async (input: CreateTicketInput) => {
      // Staff viewing the portal as a customer are read-only, and this refusal
      // is the one that holds. The page disabled its New-ticket button, but
      // three other routes opened the same dialog — the ?new=1 deep link, the
      // command palette's "New ticket" and the site panel — and none of them
      // consulted that guard. RLS does not stop it either: tickets_insert
      // admits staff on its own arm. So a support agent in view-as could file a
      // real TKT number under the customer's account, marked source 'customer',
      // with an SLA clock running and nothing in the row saying who filed it.
      // Every other portal write (comments, visit plans, preferences,
      // notifications) refuses here, inside the mutation, so no entry point can
      // route around it. This one now does too.
      if (readOnly) throw new Error('read_only');

      const { data, error } = await supabase
        .from('tickets')
        .insert({
          title: input.title,
          description: input.description || null,
          priority: input.priority,
          source: 'customer' as const,
          status: 'open' as const,
          customer_id: input.customer_id,
          // Trigger rewrites this to TKT-YYYY-NNNNN before insert.
          ticket_number: 'PENDING',
          ...(input.location_id ? { location_id: input.location_id } : {}),
          ...(input.deployment_id ? { deployment_id: input.deployment_id } : {}),
        })
        .select('id, ticket_number')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
    },
  });
}
